"use client"

import { useState, useEffect, useMemo } from "react"
import { type AnalyticsPeriod, type CustomDateRange } from "@/components/analytics-period-selector"
import { initializeAnalyticsState, saveToLocalStorage, readFromLocalStorage } from "@/lib/analytics-state"
import { getPeriodRange } from "@/lib/period-utils"

export interface AnalyticsPeriodState {
  period: AnalyticsPeriod
  setPeriod: (p: AnalyticsPeriod) => void
  includeAdmins: boolean
  setIncludeAdmins: (v: boolean) => void
  customRange: CustomDateRange | undefined
  setCustomRange: (r: CustomDateRange | undefined) => void
  /** Начало периода в секундах (unix timestamp) */
  beginTs: number
  /** Конец периода в секундах (unix timestamp) */
  endTs: number
}

/**
 * Хук для синхронизации периода аналитики через localStorage.
 * Все под-страницы аналитики (Обзор, Процедуры, Формы, Copilot) используют
 * одни и те же ключи, поэтому при смене периода на одной странице
 * другие страницы подхватят его при монтировании.
 */
export function useAnalyticsPeriod(userId?: number): AnalyticsPeriodState {
  const initialState = useMemo(() => initializeAnalyticsState(userId), [userId])

  const [period, setPeriodState] = useState<AnalyticsPeriod>(
    (initialState.period as AnalyticsPeriod) ?? 'month',
  )
  const [includeAdmins, setIncludeAdminsState] = useState<boolean>(
    initialState.includeAdmins ?? true,
  )
  const [customRange, setCustomRangeState] = useState<CustomDateRange | undefined>(() => {
    if (!initialState.customStart || !initialState.customEnd) return undefined
    const from = new Date(initialState.customStart)
    // Если конец периода был «сегодня» на момент сохранения — пролонгируем до актуального сегодня
    const to = initialState.customEndIsToday ? new Date() : new Date(initialState.customEnd)
    return { from, to }
  })

  // Обновляем состояние при смене userId (смена аккаунта)
  useEffect(() => {
    if (userId == null) return
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', window.location.pathname)
    }
    const s = readFromLocalStorage(userId)
    setPeriodState((s.period as AnalyticsPeriod) ?? 'month')
    setIncludeAdminsState(s.includeAdmins ?? true)
    setCustomRangeState(
      s.customStart && s.customEnd
        ? { from: new Date(s.customStart), to: new Date(s.customEnd) }
        : undefined,
    )
  }, [userId])

  const persist = (patch: {
    period?: AnalyticsPeriod
    includeAdmins?: boolean
    customRange?: CustomDateRange | undefined
    customEndIsToday?: boolean
  }) => {
    const s = readFromLocalStorage(userId)
    const next = {
      ...s,
      ...(patch.period !== undefined ? { period: patch.period } : {}),
      ...(patch.includeAdmins !== undefined ? { includeAdmins: patch.includeAdmins } : {}),
      ...(patch.customRange !== undefined
        ? {
            customStart: patch.customRange.from.toISOString(),
            customEnd: patch.customRange.to.toISOString(),
            customEndIsToday: patch.customEndIsToday ?? false,
          }
        : patch.customRange === undefined && patch.period !== undefined
          ? { customEndIsToday: undefined }
          : { customStart: undefined, customEnd: undefined, customEndIsToday: undefined }),
    }
    saveToLocalStorage(next, userId)
  }

  const setPeriod = (p: AnalyticsPeriod) => {
    setPeriodState(p)
    persist({ period: p })
  }

  const setIncludeAdmins = (v: boolean) => {
    setIncludeAdminsState(v)
    persist({ includeAdmins: v })
  }

  const setCustomRange = (r: CustomDateRange | undefined) => {
    setCustomRangeState(r)
    if (r) {
      const now = new Date()
      const customEndIsToday =
        r.to.getFullYear() === now.getFullYear() &&
        r.to.getMonth() === now.getMonth() &&
        r.to.getDate() === now.getDate()
      persist({ customRange: r, customEndIsToday })
    }
  }

  const periodRange = getPeriodRange(period, customRange)

  return {
    period,
    setPeriod,
    includeAdmins,
    setIncludeAdmins,
    customRange,
    setCustomRange,
    beginTs: periodRange.startTimestamp,
    endTs: periodRange.endTimestamp,
  }
}
